import {
  ProductRule,
  isRestrictedToNukiClub,
} from '@/integrations/crystallize/helpers/product-rule-shape-helper';
import type { ProductBadgeModel } from '@/models/product-badge-model';
import { getUniqueProductBadges } from '@/ui/components/product-badge/product-badge-utils';
import type { ProductVariantReference } from '@/ui/services/BaseProductService';
import type { BundleConfiguration } from '@/ui/services/BundleService';

/**
 * What the product rules of a page mean for a variant or a bundle: the badges it shows and who may
 * buy it. Products and bundles stay free of rules and only describe themselves (id, SKU, selection).
 */
export class ProductRuleService {
  private readonly rules: ReadonlyArray<ProductRule>;

  constructor(rules: ReadonlyArray<ProductRule> = []) {
    this.rules = rules;
  }

  /** Whether any item of the page can be restricted to Nuki Club members. */
  hasNukiClubRestriction(): boolean {
    return this.rules.some((rule) => isRestrictedToNukiClub(rule.restrictedTo));
  }

  getVariantBadges(variantReference: ProductVariantReference): ProductBadgeModel[] {
    return ProductRuleService.getBadges(this.getVariantRules(variantReference));
  }

  getBundleBadges(bundleConfiguration: BundleConfiguration): ProductBadgeModel[] {
    return ProductRuleService.getBadges(this.getBundleRules(bundleConfiguration));
  }

  /**
   * Badges for a product card listing some of its variants: rules targeting the product, or every
   * listed variant, so one badged colour does not badge the whole product.
   */
  getListedProductBadges(productId: string, listedSkus: string[]): ProductBadgeModel[] {
    return ProductRuleService.getBadges(
      this.rules.filter(
        (rule) =>
          !ProductRuleService.isBundleRule(rule) &&
          (rule.targetProductIds.includes(productId) ||
            (!!listedSkus.length && listedSkus.every((sku) => rule.targetSkus.includes(sku)))),
      ),
    );
  }

  isVariantRestrictedToNukiClub(variantReference: ProductVariantReference): boolean {
    return isRestrictedToNukiClub(
      ProductRuleService.getRestrictedTo(this.getVariantRules(variantReference)),
    );
  }

  isBundleRestrictedToNukiClub(bundleConfiguration: BundleConfiguration): boolean {
    return isRestrictedToNukiClub(
      ProductRuleService.getRestrictedTo(this.getBundleRules(bundleConfiguration)),
    );
  }

  isVariantShownInFeed(variantReference: ProductVariantReference): boolean {
    return this.getVariantRules(variantReference).every((rule) => rule.isShownInFeed);
  }

  isBundleShownInFeed(bundleConfiguration: BundleConfiguration): boolean {
    return this.getBundleRules(bundleConfiguration).every((rule) => rule.isShownInFeed);
  }

  private getVariantRules(variantReference: ProductVariantReference): ProductRule[] {
    return this.rules.filter((rule) =>
      ProductRuleService.doesRuleApplyToVariant(rule, variantReference));
  }

  private getBundleRules(bundleConfiguration: BundleConfiguration): ProductRule[] {
    return this.rules.filter((rule) =>
      ProductRuleService.doesRuleApplyToBundle(rule, bundleConfiguration));
  }

  private static isBundleRule(rule: ProductRule): boolean {
    return rule.targetBundleIds.length > 0;
  }

  private static hasTargets(rule: ProductRule): boolean {
    return rule.targetProductIds.length > 0 || rule.targetSkus.length > 0;
  }

  private static isTargeted(rule: ProductRule, variantReference: ProductVariantReference): boolean {
    return (
      rule.targetSkus.includes(variantReference.sku) ||
      rule.targetProductIds.includes(variantReference.productId)
    );
  }

  private static doesRuleApplyToVariant(
    rule: ProductRule,
    variantReference: ProductVariantReference,
  ): boolean {
    return (
      !ProductRuleService.isBundleRule(rule) &&
      ProductRuleService.isTargeted(rule, variantReference)
    );
  }

  private static doesRuleApplyToBundle(
    rule: ProductRule,
    bundleConfiguration: BundleConfiguration,
  ): boolean {
    if (!rule.targetBundleIds.includes(bundleConfiguration.bundleId)) {
      return false;
    }

    return (
      !ProductRuleService.hasTargets(rule) ||
      bundleConfiguration.selectedVariants.some((variantReference) =>
        ProductRuleService.isTargeted(rule, variantReference))
    );
  }

  private static getBadges(rules: ProductRule[]): ProductBadgeModel[] {
    return getUniqueProductBadges(rules.flatMap((rule) => (rule.badge ? [rule.badge] : [])));
  }

  private static getRestrictedTo(rules: ProductRule[]): string[] {
    return Array.from(new Set(rules.flatMap((rule) => rule.restrictedTo)));
  }
}
